import { MessagePathRecord } from "@/shared/domain/topics";
import { type TimeUnit } from "@/shared/time";

import { MessageFilter } from "../MessageFilter";

import type { ReaderLike, RowGroup } from "./ReaderLike";
import { convertTimestamp, timestampUnit } from "./transform";

/** A contiguous run of row indices whose timestamps match a filter. */
export interface MatchingRowRange {
  /** First row index of the run, inclusive. */
  rowStart: number;
  /** One past the last row index of the run. */
  rowEnd: number;
  /**
   * The nanosecond timestamp of every row in the run, in row order, with
   * {@link MatchingRowsOptions.offset} added.
   */
  timestamps: BigInt64Array;
}

export interface MatchingRows {
  /** The runs as a row-index filter, for readers that prune chunks by row index. */
  rowIndexFilter: MessageFilter<number>;
  /** The runs in ascending row order. Empty when no row matches. */
  ranges: MatchingRowRange[];
}

/** Where a column's timestamps are stored and how to read them. */
export interface TimestampColumn {
  /**
   * Path components from the schema root to the timestamp leaf. The first
   * names the top-level parquet column holding the timestamps; when there
   * are more, each chunk value is that column's assembled struct and the
   * leaf is read through the rest of the path.
   */
  path: string[];
  /** The unit stored values count. */
  unit: TimeUnit;
  /**
   * Whether stored values are floating point. {@link matchingRowRanges}
   * scales such values to nanoseconds in float64 and truncates them toward
   * zero.
   */
  floating?: boolean;
  /**
   * Set when the stored values are the unscaled integers of a DECIMAL
   * column of this scale; each value `v` stands for `v × 10^-decimalScale`
   * of `unit`.
   */
  decimalScale?: number;
}

export interface MatchingRowsOptions {
  /**
   * Nanoseconds added to every stored timestamp, and to the row-group
   * statistics, before either is compared with the filter. A row group is
   * skipped only when its shifted statistics lie in the signed 64-bit range
   * and rule the filter out. In every row group read, a stored value that is
   * NaN or infinite, that is a DECIMAL holding a fraction of a nanosecond, or
   * whose shifted value leaves that range throws
   * {@link InvalidTimestampError}, whether or not the filter keeps its row.
   */
  offset: bigint;
  signal: AbortSignal;
}

/**
 * A stored timestamp that is NaN or infinite, that is a DECIMAL holding a
 * fraction of a nanosecond, or whose value shifted to nanoseconds is outside
 * the signed 64-bit range.
 */
export class InvalidTimestampError extends Error {
  /** What is wrong with the stored value, naming the value, its unit and its field. */
  public readonly detail: string;
  /** The row's index in the file. */
  public readonly rowNumber: bigint;

  constructor(rowNumber: bigint, detail: string) {
    super(`The timestamp of row ${rowNumber} is invalid: ${detail}`);
    this.name = "InvalidTimestampError";
    this.rowNumber = rowNumber;
    this.detail = detail;
  }
}

const INT64_MIN = -(2n ** 63n);
const INT64_MAX = 2n ** 63n - 1n;

/** The most a statistic hyparquet parsed as a `Date` can be off, in nanoseconds. */
const DATE_STATISTIC_SLACK_NS = 999_999n;

const NANOSECONDS_PER_UNIT: Record<TimeUnit, bigint> = {
  s: 1_000_000_000n,
  ms: 1_000_000n,
  us: 1_000n,
  ns: 1n,
};

/** A contiguous run of matching rows, as the timestamp column is scanned. */
interface Run {
  rowStart: number;
  rowEnd: number;
  timestamps: bigint[];
}

/**
 * Translate a logTime window into the row indices whose stored timestamp
 * falls inside it.
 *
 * `timestamp.column` is the top-level parquet column holding the timestamps.
 * When the timestamp is a field nested inside that column (the record's
 * `path_in_schema` has more than one component and starts with the column),
 * each chunk value is the assembled struct and the leaf is read through the
 * remaining path components. Values are converted by {@link convertTimestamp}
 * with no offset. A row whose stored timestamp is null is skipped: it can
 * never fall inside a time window, and the parquet reader emits `null` for
 * such rows.
 *
 * Throws when no row matches and the column's timestamps are not in
 * ascending order.
 */
export async function convertLogtimeFilterToRowIndexFilter(
  filter: MessageFilter<bigint>,
  reader: ReaderLike,
  timestamp: { column: string; messagePath: MessagePathRecord },
): Promise<MessageFilter<number>> {
  const pathInSchema = timestamp.messagePath.path_in_schema ?? [];
  const unit = timestampUnit(timestamp.messagePath);
  const { runs, hasUnorderedTimestamps } = await scanTimestampColumn(
    filter,
    reader,
    {
      path:
        pathInSchema.length > 1 && pathInSchema[0] === timestamp.column
          ? pathInSchema
          : [timestamp.column],
      unit,
    },
    unshiftedReading(unit),
  );

  if (runs.length === 0 && hasUnorderedTimestamps) {
    throw new Error(
      `The column '${timestamp.column}' used as this topic's ` +
        "source of timestamps is not in ascending order. " +
        "This prevents selecting ranges of data by time. " +
        `Please rewrite and re-order the data by '${timestamp.column}' ASC ` +
        "and rerun ingestion, or rerun ingestion on the data as-is " +
        "using a different timestamp column.",
    );
  }
  // When no row matches, the filter returned matches nothing, so the caller
  // yields no data for this file. That is not an error: when several files or
  // topics are plotted on one panel, a zoom or pan can request a window
  // entirely outside one file's data, and failing that file would fail the
  // panel.
  return rowIndexFilterOf(runs);
}

/**
 * The rows whose timestamp, shifted by `options.offset`, matches `filter`,
 * as contiguous runs that carry those timestamps, so a consumer that emits
 * the rows does not decode the timestamp column a second time. A row is in a
 * run if and only if its stored timestamp is non-null and its shifted value
 * matches the filter. Rows may be stored in any time order.
 */
export async function matchingRowRanges(
  filter: MessageFilter<bigint>,
  reader: ReaderLike,
  timestamp: TimestampColumn,
  options: MatchingRowsOptions,
): Promise<MatchingRows> {
  const { runs } = await scanTimestampColumn(
    filter,
    reader,
    timestamp,
    shiftedReading(timestamp, options.offset),
    options.signal,
  );
  return {
    rowIndexFilter: rowIndexFilterOf(runs),
    ranges: runs.map((run) => ({
      rowStart: run.rowStart,
      rowEnd: run.rowEnd,
      timestamps: BigInt64Array.from(run.timestamps),
    })),
  };
}

/**
 * Whether any row index in `bounds` (`rowStart` inclusive, `rowEnd`
 * exclusive) is matched by `filter`. Range filter bounds are inclusive.
 */
export function matchesFilter(
  bounds: { rowStart: number; rowEnd: number },
  filter: MessageFilter<number>,
): boolean {
  const { rowStart, rowEnd } = bounds;
  if (filter.isRangeFilter()) {
    const { min = rowStart, max = rowEnd - 1 } = filter;
    return min < rowEnd && max >= rowStart;
  }

  if (filter.isListFilter()) {
    return filter.matching.some((index) => index >= rowStart && index < rowEnd);
  }

  if (filter.isCombinedFilter()) {
    return filter.filters.some((subfilter) => matchesFilter(bounds, subfilter));
  }
  return false;
}

/**
 * Read the timestamp column in the row groups whose statistics do not rule
 * `filter` out, and collect the runs of rows whose timestamp, as `reading`
 * gives it, matches `filter`. Rows whose stored timestamp is null are
 * skipped.
 */
async function scanTimestampColumn(
  filter: MessageFilter<bigint>,
  reader: ReaderLike,
  timestamp: TimestampColumn,
  reading: TimestampReading,
  signal?: AbortSignal,
): Promise<{ runs: Run[]; hasUnorderedTimestamps: boolean }> {
  const [column, ...nestedPath] = timestamp.path;
  const runs: Run[] = [];
  /** Whether any chunk's timestamps decrease, to explain an empty result. */
  let hasUnorderedTimestamps = false;
  for await (const chunk of reader.iterColumnChunks({
    columns: [column],
    rowGroupFilter: (rowGroup: RowGroup) =>
      rowGroupMayMatch(rowGroup, timestamp, reading, filter),
    signal,
  })) {
    if (chunk.columnName !== column) {
      continue;
    }
    let lastTimestamp: bigint | undefined;
    for (const [index, row] of chunk.columnData.entries()) {
      const storedValue = storedTimestampValue(row, nestedPath);
      if (storedValue === null || storedValue === undefined) {
        continue;
      }
      const rowIndex = index + chunk.rowStart;
      const timestampValue = reading.value(storedValue, rowIndex);
      if (lastTimestamp !== undefined && timestampValue < lastTimestamp) {
        hasUnorderedTimestamps = true;
      }
      if (filter.matches(timestampValue)) {
        const lastRun = runs.at(-1);
        if (lastRun !== undefined && lastRun.rowEnd === rowIndex) {
          lastRun.rowEnd = rowIndex + 1;
          lastRun.timestamps.push(timestampValue);
        } else {
          runs.push({
            rowStart: rowIndex,
            rowEnd: rowIndex + 1,
            timestamps: [timestampValue],
          });
        }
      }
      lastTimestamp = timestampValue;
    }
  }
  return { runs, hasUnorderedTimestamps };
}

/** A row-index filter matching exactly the rows of `runs`. */
function rowIndexFilterOf(runs: Run[]): MessageFilter<number> {
  if (runs.length === 0) {
    return MessageFilter.fromList<number>([]);
  }
  const rangeFilters = runs.map((run) =>
    MessageFilter.fromRange<number>({ min: run.rowStart, max: run.rowEnd - 1 }),
  );
  return rangeFilters.length === 1
    ? rangeFilters[0]
    : MessageFilter.combine(rangeFilters);
}

/** How stored timestamps and their statistics become comparable nanoseconds. */
interface TimestampReading {
  /** The nanoseconds `stored` stands for; `rowIndex` names it in errors. */
  value(stored: unknown, rowIndex: number): bigint;
  /**
   * The nanoseconds a column chunk's min or max statistic bounds its values
   * by, or undefined when the statistic cannot rule a row group out.
   */
  bound(statistic: unknown, side: "min" | "max"): bigint | undefined;
}

/**
 * Stored values converted by {@link convertTimestamp}, which rounds numbers
 * to the nearest nanosecond, with no offset and no range check. A string
 * statistic bounds nothing, since strings order as text rather than as
 * times.
 */
function unshiftedReading(unit: TimeUnit): TimestampReading {
  return {
    value: (stored) => convertTimestamp(stored, unit),
    bound: (statistic, side) => {
      if (statistic instanceof Date) {
        return dateStatisticBound(statistic, side);
      }
      return isFiniteNumeric(statistic)
        ? convertTimestamp(statistic, unit)
        : undefined;
    },
  };
}

/** Stored values shifted by `offset`, checked to stay in the signed 64-bit range. */
function shiftedReading(
  timestamp: TimestampColumn,
  offset: bigint,
): TimestampReading {
  const multiplier = NANOSECONDS_PER_UNIT[timestamp.unit];
  const floating = timestamp.floating === true;
  const divisor =
    timestamp.decimalScale === undefined
      ? undefined
      : 10n ** BigInt(timestamp.decimalScale);
  const nanoseconds = (stored: number | bigint): bigint | undefined => {
    if (typeof stored === "bigint") {
      // For a row's value the division leaves no remainder, since `value`
      // first refuses a DECIMAL holding a fraction of a nanosecond. A
      // statistic truncated toward zero still bounds every whole-nanosecond
      // value in its row group.
      return divisor === undefined
        ? stored * multiplier
        : (stored * multiplier) / divisor;
    }
    if (!floating) {
      return BigInt(stored) * multiplier;
    }
    const scaled = Math.trunc(stored * Number(multiplier));
    return Number.isFinite(scaled) ? BigInt(scaled) : undefined;
  };
  return {
    value(stored, rowIndex) {
      if (typeof stored !== "number" && typeof stored !== "bigint") {
        throw new Error(
          `The timestamp column "${timestamp.path.join(".")}" holds a ${typeof stored} at row ${rowIndex}; timestamps must be numbers`,
        );
      }
      if (
        typeof stored === "bigint" &&
        divisor !== undefined &&
        (stored * multiplier) % divisor !== 0n
      ) {
        throw new InvalidTimestampError(
          BigInt(rowIndex),
          `${stored} × 10^-${timestamp.decimalScale} ${timestamp.unit} of "${timestamp.path.join(".")}" is not a whole number of nanoseconds`,
        );
      }
      const scaled = nanoseconds(stored);
      const shifted = scaled === undefined ? undefined : scaled + offset;
      if (shifted === undefined || shifted < INT64_MIN || shifted > INT64_MAX) {
        throw new InvalidTimestampError(
          BigInt(rowIndex),
          `${String(stored)} ${timestamp.unit} of "${timestamp.path.join(".")}", shifted by ${offset} ns, is not a signed 64-bit nanosecond value`,
        );
      }
      return shifted;
    },
    bound(statistic, side) {
      let scaled: bigint | undefined;
      if (statistic instanceof Date) {
        scaled = dateStatisticBound(statistic, side);
      } else if (typeof statistic === "number" && divisor !== undefined) {
        scaled = decimalStatisticBound(statistic, multiplier, side);
      } else if (isFiniteNumeric(statistic)) {
        scaled = nanoseconds(statistic);
      }
      const shifted = scaled === undefined ? undefined : scaled + offset;
      return shifted === undefined || shifted < INT64_MIN || shifted > INT64_MAX
        ? undefined
        : shifted;
    },
  };
}

function isFiniteNumeric(value: unknown): value is number | bigint {
  return (
    typeof value === "bigint" ||
    (typeof value === "number" && Number.isFinite(value))
  );
}

/**
 * The nanosecond bound a `Date` statistic stands for, or undefined for an
 * invalid `Date`, which hyparquet gives for a value outside the range a
 * `Date` holds. hyparquet parses a timestamp column's statistics as a
 * `Date`, cutting the value toward zero to whole milliseconds, which moves
 * it by up to 999,999 ns: earlier for a value after the epoch, later for one
 * before it. Widening each bound by that much on its own side covers both.
 */
function dateStatisticBound(
  statistic: Date,
  side: "min" | "max",
): bigint | undefined {
  const milliseconds = statistic.getTime();
  if (Number.isNaN(milliseconds)) {
    return undefined;
  }
  const nanoseconds = BigInt(milliseconds) * 1_000_000n;
  return side === "min"
    ? nanoseconds - DATE_STATISTIC_SLACK_NS
    : nanoseconds + DATE_STATISTIC_SLACK_NS;
}

/**
 * The nanosecond bound for a DECIMAL column's statistic, which counts units
 * of `multiplier` nanoseconds, or undefined when the scaled statistic is not
 * finite. hyparquet parses a DECIMAL statistic as a float64 number with
 * three roundings (the unscaled integer, the power of ten, and their
 * product), and scaling it here adds a fourth, so the result is off from the
 * stored decimal by less than 2^-50 of its size. Widening each bound by
 * 2^-48 of its size, plus one nanosecond for the truncation, covers that
 * error.
 */
function decimalStatisticBound(
  statistic: number,
  multiplier: bigint,
  side: "min" | "max",
): bigint | undefined {
  const scaled = statistic * Number(multiplier);
  if (!Number.isFinite(scaled)) {
    return undefined;
  }
  const slack = BigInt(Math.ceil(Math.abs(scaled) * 2 ** -48)) + 1n;
  const truncated = BigInt(Math.trunc(scaled));
  return side === "min" ? truncated - slack : truncated + slack;
}

function isStructValue(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Date)
  );
}

/**
 * The stored timestamp in one chunk value: the value itself when
 * `nestedPath` is empty, else the leaf reached through `nestedPath`, or
 * undefined when a struct on the way is null or lacks the next field.
 */
function storedTimestampValue(value: unknown, nestedPath: string[]): unknown {
  let current = value;
  for (const component of nestedPath) {
    if (!isStructValue(current)) {
      return undefined;
    }
    current = current[component];
  }
  return current;
}

function rowGroupMayMatch(
  rowGroup: RowGroup,
  timestamp: TimestampColumn,
  reading: TimestampReading,
  filter: MessageFilter<bigint>,
) {
  // Column chunk statistics live on the leaf column, whose path in the
  // parquet schema is the timestamp's full path.
  const stats =
    rowGroup.columns.find((col) =>
      isSamePath(col.meta_data?.path_in_schema, timestamp.path),
    )?.meta_data?.statistics ?? {};

  if (stats.min_value === undefined || stats.max_value === undefined) {
    return true;
  }
  const minValue = reading.bound(stats.min_value, "min");
  const maxValue = reading.bound(stats.max_value, "max");
  if (minValue === undefined || maxValue === undefined) {
    return true;
  }
  return statisticsMatch(minValue, maxValue, filter);
}

function isSamePath(
  path: readonly string[] | undefined,
  other: readonly string[],
): boolean {
  return (
    path !== undefined &&
    path.length === other.length &&
    path.every((name, index) => name === other[index])
  );
}

/** Whether any value in `[minValue, maxValue]` can match `filter`. */
function statisticsMatch(
  minValue: bigint,
  maxValue: bigint,
  filter: MessageFilter<bigint>,
): boolean {
  if (filter.isRangeFilter()) {
    const filterStart = filter.min ?? minValue;
    const filterEnd = filter.max ?? maxValue;
    return filterStart <= maxValue && filterEnd >= minValue;
  }

  if (filter.isListFilter()) {
    return filter.matching.some(
      (value) => value >= minValue && value <= maxValue,
    );
  }

  if (filter.isCombinedFilter()) {
    // A combined filter matches a value that any of its filters matches.
    return filter.filters.some((subfilter) =>
      statisticsMatch(minValue, maxValue, subfilter),
    );
  }

  return false;
}
